// What has been delivered for the signed-in user, per product and country: one status for every
// country x product, including the states a list of "countries with data" leaves out (no access,
// nothing delivered, not offered, demo). A thin binding over availabilityStore, which already
// shares and caches the per-country requests with the data pages.
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { COUNTRY_CODES, COUNTRY_LIST } from '../config/countries';
import type { CountryCode } from '../config/countries';
import { DATA_PRODUCT_IDS, countrySupport } from '../config/products';
import type { DataProductId } from '../config/products';
import { useAuth } from '../contexts/AuthContext';
import { availabilityStore } from '../services/availabilityStore';
import type { AvailabilityState } from '../services/availabilityStore';
import { demoServes } from '../services/demoApi';
import type { IsoDay } from '../utils/availability';
import { productDays } from '../utils/coverage';

export type CoverageStatus =
  | { kind: 'delivered'; dates: readonly IsoDay[]; latest: IsoDay }
  | { kind: 'checking' } // idle or loading
  | { kind: 'none' } // ready, 0 usable days
  | { kind: 'no-access' } // 403
  | { kind: 'unknown'; retryable: boolean } // 404 'unavailable', error, or no signed-in user
  | { kind: 'not-offered'; reason: string; alternatives: CountryCode[] }
  | { kind: 'demo'; dates: readonly IsoDay[] }; // demo-served

export interface CoverageRow {
  country: CountryCode;
  status: CoverageStatus;
}

/** The status of `p` in `c` from the registry, the availability state and the demo flag. */
export function coverageStatus(
  c: CountryCode,
  p: DataProductId,
  state: AvailabilityState,
  demo: boolean,
): CoverageStatus {
  if (demo) return { kind: 'demo', dates: state.status === 'ready' ? productDays(c, p, state.index) : [] };

  const support = countrySupport(c, p);
  if (!support.ok) return { kind: 'not-offered', reason: support.reason, alternatives: support.alternatives };

  switch (state.status) {
    case 'idle':
    case 'loading':
      return { kind: 'checking' };
    case 'ready': {
      const dates = productDays(c, p, state.index);
      return dates.length > 0 ? { kind: 'delivered', dates, latest: dates[dates.length - 1] } : { kind: 'none' };
    }
    case 'no-access':
      return { kind: 'no-access' };
    case 'unavailable':
      return { kind: 'unknown', retryable: false };
    case 'error':
      return { kind: 'unknown', retryable: state.retryable };
  }
}

/** True when a person can open the product in this country. */
export function isChoosable(s: CoverageStatus): boolean {
  return s.kind === 'delivered' || s.kind === 'checking' || s.kind === 'unknown' || s.kind === 'demo';
}

export interface CoverageTable {
  /** Per product, one row per registry country in COUNTRY_LIST order. Products not asked for are []. */
  rows: Readonly<Record<DataProductId, readonly CoverageRow[]>>;
  /** True while any fetched country is idle or loading. */
  loading: boolean;
  retry(c: CountryCode): void;
}

type Snapshot = Omit<CoverageTable, 'retry'>;

const IDLE: AvailabilityState = Object.freeze({ status: 'idle' as const });
// With nobody signed in nothing is coming, and the answer cannot be known.
const NO_USER: AvailabilityState = Object.freeze({ status: 'unavailable' as const });

const SINGLE: Readonly<Record<DataProductId, readonly DataProductId[]>> = {
  activities: ['activities'],
  odm: ['odm'],
  flow: ['flow'],
};

function build(
  products: readonly DataProductId[],
  fetched: readonly CountryCode[],
  states: readonly AvailabilityState[],
  signedIn: boolean,
): Snapshot {
  const stateOf = (c: CountryCode) => states[fetched.indexOf(c)] ?? IDLE;
  const rows: Record<DataProductId, CoverageRow[]> = { activities: [], odm: [], flow: [] };
  for (const p of products) {
    rows[p] = COUNTRY_LIST.map(({ code }) => ({
      country: code,
      status: coverageStatus(code, p, stateOf(code), demoServes(code, p)),
    }));
  }
  const loading = signedIn && states.some((s) => s.status === 'idle' || s.status === 'loading');
  return { rows, loading };
}

/**
 * Coverage for one product (a chooser) or, with no argument, every data product (the coverage
 * page). Asks the store only for countries that offer one of the products. The returned object is
 * the same one until a country's state changes.
 */
export function useCoverage(product?: DataProductId): CoverageTable {
  const uid = useAuth().currentUser?.uid ?? null;
  const { get, ensure, refresh, subscribe } = availabilityStore;
  const products = product ? SINGLE[product] : DATA_PRODUCT_IDS;

  const fetched = useMemo(
    () => COUNTRY_CODES.filter((c) => products.some((p) => countrySupport(c, p).ok || demoServes(c, p))),
    [products],
  );

  useEffect(() => {
    if (!uid) return;
    for (const c of fetched) ensure(uid, c);
  }, [uid, ensure, fetched]);

  const cache = useRef<{ products: readonly DataProductId[]; states: AvailabilityState[]; value: Snapshot } | null>(null);
  const getSnapshot = useCallback((): Snapshot => {
    const states = fetched.map((c) => (uid ? get(uid, c) : NO_USER));
    const prev = cache.current;
    if (prev && prev.products === products && prev.states.length === states.length && prev.states.every((s, i) => s === states[i])) {
      return prev.value;
    }
    cache.current = { products, states, value: build(products, fetched, states, uid !== null) };
    return cache.current.value;
  }, [uid, get, products, fetched]);

  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const retry = useCallback(
    (c: CountryCode) => {
      if (uid) refresh(uid, c);
    },
    [uid, refresh],
  );

  return useMemo<CoverageTable>(() => ({ ...snap, retry }), [snap, retry]);
}
